import { useTranslation } from 'react-i18next'
import type { PublicJobcardView, PublicInspection } from '@garage/shared'
import { publicApi } from '@/lib/public-api.js'

interface PublicInspectionSheetProps {
  garage: PublicJobcardView['garage']
  jobcardNumber: string
  car: PublicJobcardView['car']
  inspection: PublicInspection
  token: string
}

/**
 * Printable A4 inspection report rendered with plain CSS (no Tailwind).
 * Designed to look like the staff InspectionPrintView.
 */
export function PublicInspectionSheet({
  garage,
  jobcardNumber,
  car,
  inspection,
  token,
}: PublicInspectionSheetProps) {
  const { t, i18n } = useTranslation()
  const isRTL = i18n.dir() === 'rtl'

  const scaleOptionLabel = (scaleKey: string, value: string, fallback: string) =>
    t(`inspection.scales.${scaleKey}.${value}`, fallback)

  const overviewAngleLabel = (angle: string, fallback: string) =>
    t(`inspection.overviewAngles.${angle}`, fallback)

  const cssText = isRTL ? getArabicPrintCSS() : PRINT_CSS

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} style={{ colorScheme: 'light', backgroundColor: '#fff' }}>
      <style>{cssText}</style>
      <div className="page">
        {/* Header */}
        <header className="header">
          {garage.hasLogo && (
            <img
              className="logo"
              src="/api/public/logo"
              alt=""
              onError={(e) => {
                e.currentTarget.style.display = 'none'
              }}
            />
          )}
          <div className="header-text">
            <div className="business-name">{garage.name}</div>
            <h1 className="title">{t('inspectionPrint.vehicleInspectionReport')}</h1>
          </div>
          <div className="header-meta">
            <div>
              <span className="label">{t('inspectionPrint.jobNo')}</span> <bdi>{jobcardNumber}</bdi>
            </div>
            {inspection.performedAt && (
              <div>
                <span className="label">{t('calendar.date')}</span> <bdi>{inspection.performedAt}</bdi>
              </div>
            )}
          </div>
        </header>

        {/* Vehicle info */}
        <section className="grid">
          <div className="box">
            <h2>{t('carSelector.vehicleAriaLabel')}</h2>
            <div className="row">
              <span className="label">{t('carForm.brand')}</span>
              <span>{car.brand}</span>
            </div>
            <div className="row">
              <span className="label">{t('carForm.model')}</span>
              <span>{car.model}</span>
            </div>
            {car.year && (
              <div className="row">
                <span className="label">{t('carForm.year')}</span>
                <span>{car.year}</span>
              </div>
            )}
            <div className="row">
              <span className="label">{t('carForm.plate')}</span>
              <bdi>{car.plate}</bdi>
            </div>
            {car.color && (
              <div className="row">
                <span className="label">{t('carForm.colour')}</span>
                <span>{car.color}</span>
              </div>
            )}
          </div>

          <div className="box">
            <h2>{t('inspectionPrint.inspection')}</h2>
            {inspection.performedBy && (
              <div className="row">
                <span className="label">{t('inspectionPrint.inspectedBy')}</span>
                <bdi>{inspection.performedBy}</bdi>
              </div>
            )}
            {inspection.performedAt && (
              <div className="row">
                <span className="label">{t('calendar.date')}</span>
                <span>{inspection.performedAt}</span>
              </div>
            )}
          </div>
        </section>

        {/* Overview photos */}
        {inspection.overview.length > 0 && (
          <section className="box section">
            <h2>{t('inspection.vehiclePhotos')}</h2>
            <div className="overview-photos">
              {inspection.overview.map((photo) => (
                <div className="overview-photo" key={photo.angle}>
                  <img
                    src={publicApi.inspectionOverviewPhotoUrl(token, photo.angle, 1600)}
                    alt={overviewAngleLabel(photo.angle, photo.label)}
                  />
                  <span>{overviewAngleLabel(photo.angle, photo.label)}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Summary */}
        <section className="box">
          <h2>{t('publicPortal.inspection.heading')}</h2>
          <div className="summary-row">
            <span className="label">{t('publicPortal.inspection.itemsChecked_other', { count: inspection.summary.rated })}</span>
            <span>{inspection.summary.rated}</span>
          </div>
          <div className="summary-row">
            <span className="label">{t('publicPortal.inspection.good_other', { count: inspection.summary.good })}</span>
            <span style={{ color: '#2f7d54' }}>{inspection.summary.good}</span>
          </div>
          {inspection.summary.attention > 0 && (
            <div className="summary-row">
              <span className="label">{t('publicPortal.inspection.needsAttention_other', { count: inspection.summary.attention })}</span>
              <span style={{ color: '#c0392b' }}>{inspection.summary.attention}</span>
            </div>
          )}
        </section>

        {/* Inspection sections */}
        {inspection.sections.map((section) => (
          <section className="box section" key={section.id}>
            <h2>{section.label}</h2>
            {section.note && <p className="section-note" dir="auto">{section.note}</p>}
            <table className="items">
              {section.items.map((item) => {
                const toneColor = {
                  success: '#2f7d54',
                  warning: '#9a6f14',
                  error: '#c0392b',
                  neutral: '#999999',
                }[item.tone] || '#999999'
                return (
                  <tbody key={item.id} className="item-group">
                    <tr>
                      <td className="item-label">{item.label}</td>
                      <td className="item-status" style={{ color: toneColor }}>
                        {scaleOptionLabel(item.scale, item.status, item.statusLabel)}
                      </td>
                      <td className="item-comment" dir="auto">{item.comment || ''}</td>
                    </tr>
                    {item.hasPhoto && (
                      <tr className="item-photo-row">
                        <td colSpan={3} className="item-photo-cell">
                          <img src={publicApi.inspectionItemPhotoUrl(token, item.id, 1600)} alt={item.label} />
                        </td>
                      </tr>
                    )}
                  </tbody>
                )
              })}
            </table>
          </section>
        ))}

        {/* Overall notes */}
        {inspection.overallNotes && (
          <section className="box">
            <h2>{t('inspectionPrint.overallNotes')}</h2>
            <p className="section-note" dir="auto">{inspection.overallNotes}</p>
          </section>
        )}

        {/* Footer */}
        <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #ccc', fontSize: '11px', color: '#999' }}>
          {t('publicPortal.inspection.heading')} • {jobcardNumber} • {new Date().toLocaleDateString(i18n.language === 'ar' ? 'ar-u-nu-latn' : 'en-US')}
        </div>
      </div>
    </div>
  )
}

const PRINT_CSS = `
  @page {
    size: A4;
    margin: 16mm;
  }

  .page {
    max-width: 210mm;
    margin: 0 auto;
    padding: 16px;
    font-family: Arial, Helvetica, sans-serif;
    color: #111;
    font-size: 14px;
    line-height: 1.5;
  }

  .header {
    display: flex;
    align-items: center;
    gap: 16px;
    border-bottom: 3px solid #111;
    padding-bottom: 12px;
    margin-bottom: 16px;
  }
  .logo {
    height: 56px;
    width: auto;
    object-fit: contain;
  }
  .header-text {
    flex: 1;
  }
  .business-name {
    font-size: 13px;
    color: #555;
  }
  .title {
    margin: 2px 0 0;
    font-size: 22px;
    letter-spacing: 1px;
    text-transform: uppercase;
  }
  .header-meta {
    text-align: right;
    font-size: 13px;
    line-height: 1.6;
    white-space: nowrap;
  }

  .grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 16px;
  }

  .box {
    border: 1px solid #ccc;
    border-radius: 6px;
    padding: 12px 16px;
    margin-bottom: 16px;
  }
  .box h2 {
    margin: 0 0 8px;
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: #555;
    font-weight: 600;
  }

  .row, .summary-row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 4px 0;
    border-bottom: 1px solid #eee;
    font-size: 14px;
  }
  .row:last-child, .summary-row:last-child {
    border-bottom: none;
  }

  .label {
    font-weight: 600;
    color: #555;
    min-width: 80px;
  }

  .section {
    break-inside: avoid;
  }
  .section-note {
    margin: 0 0 8px;
    font-size: 13px;
    color: #444;
    font-style: italic;
  }

  .items {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
  }
  .item-group {
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .items tr {
    border-bottom: 1px solid #eee;
  }
  .items tbody:last-child tr:last-child {
    border-bottom: none;
  }
  .items td {
    padding: 5px 4px;
    vertical-align: top;
  }
  .item-label {
    width: 40%;
  }
  .item-status {
    width: 14%;
    font-weight: 700;
    text-transform: uppercase;
    font-size: 11px;
    letter-spacing: 0.5px;
  }
  .item-comment {
    width: 46%;
    color: #444;
  }
  .item-photo-row {
    background: #fafafa;
  }
  .item-photo-cell {
    padding: 8px 4px;
    text-align: start;
  }
  .item-photo-cell img {
    max-width: 240px;
    max-height: 180px;
    width: auto;
    height: auto;
    object-fit: contain;
    border-radius: 4px;
    border: 1px solid #ccc;
  }

  .overview-photos {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }
  .overview-photo {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    break-inside: avoid;
  }
  .overview-photo img {
    width: 100%;
    aspect-ratio: 4/3;
    object-fit: cover;
    border-radius: 4px;
    border: 1px solid #ccc;
  }
  .overview-photo span {
    font-size: 11px;
    color: #555;
    text-align: center;
  }

  @media print {
    .page {
      padding: 0;
      max-width: none;
    }
    .box {
      break-inside: avoid;
    }
  }
`

/**
 * Arabic-specific CSS that disables letter-spacing and text-transform
 * for all text elements, as html2canvas destroys Arabic text joining when
 * letter-spacing is non-zero or text-transform is applied.
 */
function getArabicPrintCSS(): string {
  return `
  @page {
    size: A4;
    margin: 16mm;
  }

  .page {
    max-width: 210mm;
    margin: 0 auto;
    padding: 16px;
    font-family: Arial, Helvetica, sans-serif;
    color: #111;
    font-size: 14px;
    line-height: 1.5;
    letter-spacing: normal !important;
    text-transform: none !important;
  }

  .header {
    display: flex;
    align-items: center;
    gap: 16px;
    border-bottom: 3px solid #111;
    padding-bottom: 12px;
    margin-bottom: 16px;
  }
  .logo {
    height: 56px;
    width: auto;
    object-fit: contain;
  }
  .header-text {
    flex: 1;
  }
  .business-name {
    font-size: 13px;
    color: #555;
    letter-spacing: normal !important;
    text-transform: none !important;
  }
  .title {
    margin: 2px 0 0;
    font-size: 22px;
    color: #555;
    font-weight: 600;
    letter-spacing: normal !important;
    text-transform: none !important;
  }
  .header-meta {
    text-align: right;
    font-size: 13px;
    line-height: 1.6;
    white-space: nowrap;
  }

  .grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 16px;
  }

  .box {
    border: 1px solid #ccc;
    border-radius: 6px;
    padding: 12px 16px;
    margin-bottom: 16px;
  }
  .box h2 {
    margin: 0 0 8px;
    font-size: 13px;
    color: #555;
    font-weight: 600;
    letter-spacing: normal !important;
    text-transform: none !important;
  }

  .row, .summary-row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 4px 0;
    border-bottom: 1px solid #eee;
    font-size: 14px;
  }
  .row:last-child, .summary-row:last-child {
    border-bottom: none;
  }

  .label {
    font-weight: 600;
    color: #555;
    min-width: 80px;
    letter-spacing: normal !important;
    text-transform: none !important;
  }

  .section {
    break-inside: avoid;
  }
  .section-note {
    margin: 0 0 8px;
    font-size: 13px;
    color: #444;
    font-style: italic;
    letter-spacing: normal !important;
    text-transform: none !important;
  }

  .items {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
  }
  .item-group {
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .items tr {
    border-bottom: 1px solid #eee;
  }
  .items tbody:last-child tr:last-child {
    border-bottom: none;
  }
  .items td {
    padding: 5px 4px;
    vertical-align: top;
  }
  .item-label {
    width: 40%;
    letter-spacing: normal !important;
    text-transform: none !important;
  }
  .item-status {
    width: 14%;
    font-weight: 700;
    font-size: 11px;
    letter-spacing: normal !important;
    text-transform: none !important;
  }
  .item-comment {
    width: 46%;
    color: #444;
    letter-spacing: normal !important;
    text-transform: none !important;
  }
  .item-photo-row {
    background: #fafafa;
  }
  .item-photo-cell {
    padding: 8px 4px;
    text-align: start;
  }
  .item-photo-cell img {
    max-width: 240px;
    max-height: 180px;
    width: auto;
    height: auto;
    object-fit: contain;
    border-radius: 4px;
    border: 1px solid #ccc;
    letter-spacing: normal !important;
    text-transform: none !important;
  }

  .overview-photos {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }
  .overview-photo {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    break-inside: avoid;
  }
  .overview-photo img {
    width: 100%;
    aspect-ratio: 4/3;
    object-fit: cover;
    border-radius: 4px;
    border: 1px solid #ccc;
  }
  .overview-photo span {
    font-size: 11px;
    color: #555;
    text-align: center;
    letter-spacing: normal !important;
    text-transform: none !important;
  }

  @media print {
    .page {
      padding: 0;
      max-width: none;
    }
    .box {
      break-inside: avoid;
    }
  }
  `
}
